import Deduction from "../../../model/Deduction";
import {Box, IconButton, InputAdornment, Paper, Typography, useTheme} from "@mui/material";
import * as React from "react";
import {useEffect, useState} from "react";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreateDeductionInput, UpdateDeductionInput} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";
import Model from "../../../model/Model";
import Schedule, {Frequency} from "../../../model/Schedule";
import ScheduleSelector from "../../../components/controls/ScheduleSelector";
import FormGroupSpacer from "../../../components/form/FormGroupSpacer";
import Asset, {AssetCategory} from "../../../model/Asset";
import MilestoneDateFieldValidator from "../../../components/form/MilestoneDateFieldValidator";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import {
  getISODateFromDate,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import {isNumber} from "../../../model/ModelUtilities";
import Tracking from "../../../components/Tracking";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Snapshot from "../../../model/Snapshot";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import FormValidator from "../../../components/form/FormValidator";
import DialogButton from "../../../components/form/DialogButton";
import Income from "../../../model/Income";

const DeductionEditDialog = ({
  open,
  deduction,
  model,
  snapshot,
  onClose,
  onSave,
  onDelete,
  onArchive,
  quickForm
}: {
  open? : boolean
  deduction: Deduction
  model: Model
  snapshot?: Snapshot
  onClose?(): void
  onSave?(update: Deduction): void
  onDelete?(deduction: Deduction): void
  onArchive?(deduction: Deduction): void
  quickForm?: boolean
}) => {
  const [deductionModel, setDeductionModel] = useState<Deduction>(deduction)
  const [description, setDescription] = useState<string>("")
  const [amount, setAmount] = useState<string | undefined>()
  const [amountDate, setAmountDate] = useState<Date | undefined>()
  const [asset, setAsset] = useState<Asset | null>(null)
  const [owner, setOwner] = useState<string | undefined>()
  const [schedule, setSchedule] = useState<Schedule | undefined>()
  const [start, setStart] = useState<string>()
  const [end, setEnd] = useState<string>()
  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [annualInfLock, setAnnualInfLock] = useState<"Model" | "User" | undefined>()
  const [archived, setArchived] = useState<boolean>(false)
  const [income, setIncome] = useState<Income | undefined>()
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const { modelStore, userStore, notify } = useResources()
  const theme = useTheme()

  // const styles = {
  //   actions: {
  //     display:"flex",
  //     flexDirection:"row",
  //     width:"100%",
  //     justifyContent:"space-between",
  //     alignItems:"center",
  //     marginTop: theme.spacing(1)
  //   },
  //   row: {
  //     maxHeight: 48
  //   },
  //   formLabel: {
  //     fontSize: 12,
  //     fontWeight: 400,
  //     color: theme.palette.grey["700"]
  //   },
  //   stateField: {
  //     top: 5,
  //     width: "100%"
  //   }
  // }

  useEffect(() => {
    setDeductionModel(deduction)
    setDescription(deduction.description)
    // const itemAmount = modelStore.getBudgetItemAmountDetail(deduction, model, snapshot)
    // setAmount(numberToMoneyFormat(itemAmount.amount, 0))
    // setAmountDate(itemAmount.amountDate)
    setAmount(numberToMoneyFormat(deduction.amount, 0))
    setAmountDate(deduction.amountDate)
    const asset = model.getAssetById(deduction.assetId)
    setAsset(asset ?? null)
    let person = deduction.ownerId ? model.getPerson(deduction.ownerId) : undefined
    if (!person) {
      person = asset && asset.ownerId ? model.getPerson(asset.ownerId) : undefined
    }
    setOwner(person ? person.nickname : model.getDefaultPersonNickname())
    setSchedule(deduction.schedule ?? new Schedule({name: "Monthly", frequency: Frequency.Monthly, interval: 1}))
    setStart(deduction.start)
    setEnd(deduction.end)
    setAnnualInf(isNumber(deduction.annualInf) ? numberToPercentFormat(deduction.annualInf, 2) : "")
    if (deduction.infLock) {
      setAnnualInfLock("User")
    } else if (model.hasLock(deduction.id, "annualInf")) {
      setAnnualInfLock("Model")
    }
    if (model.hasLock(deduction.id, "*") || model.isLocked() || deduction.paycheckId) {
      setIsLocked(true)
    }
    if (deduction.paycheckId) {
      setIncome(model.getIncomeById(deduction.paycheckId))
    }
    setArchived(deduction.archived)
    setIsOpen(open === true)
  }, [deduction, model, open])

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    try {
      let updatedDeduction: Deduction | undefined
      const amountValue = moneyToNumberFormat(amount, 0) ?? 0

      if (!deductionModel.createdAt) {
        const input: CreateDeductionInput = {
          id: deductionModel.id,
          accountId: deductionModel.accountId,
          userId: deductionModel.userId,
          modelId: deductionModel.modelId,
          description: description,
          amount: amountValue,
          amountDate: amountDate ? getISODateFromDate(amountDate) : null,
          schedule: JSON.stringify(schedule),
          start: start,
          end: end ?? null,
          annualInf: annualInf !== undefined ? parseFloat(annualInf) / 100.0 : null,
          infLock: annualInfLock === "User",
          assetId: asset ? asset.id : null,
          ownerId: owner ? model.getPersonByNickname(owner)?.id : null,
          sortOrder: deductionModel.sortOrder
        }

        updatedDeduction = await modelStore.createDeduction(input)
        Tracking.event({action: "Deduction Created"})
      } else {
        const input: UpdateDeductionInput = {
          id: deductionModel.id,
          accountId: deductionModel.accountId,
          userId: deductionModel.userId,
          modelId: deductionModel.modelId,
          description: description,
          amount: amountValue,
          amountDate: snapshot ? getISODateFromDate(snapshot.date) : null,
          schedule: JSON.stringify(schedule),
          start: start,
          end: end ?? null,
          annualInf: annualInf !== undefined ? parseFloat(annualInf) / 100.0 : null,
          infLock: annualInfLock === "User",
          assetId: asset ? asset.id : null,
          ownerId: owner ? model.getPersonByNickname(owner)?.id : null,
        }

        if (annualInfLock === "Model") {
          delete input.annualInf
        }

        updatedDeduction = await modelStore.updateDeduction(input)
        Tracking.event({action: "Deduction Updated"})
      }

      if (onSave && updatedDeduction) {
        onSave(updatedDeduction)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const handleArchive = async () => {
    if (onArchive) {
      if (deductionModel.createdAt) {
        const deduction = await modelStore.updateDeduction({id: deductionModel.id, archived: !deductionModel.archived})
        if (deduction) {
          onArchive(deduction)
        }
      } else if (onDelete) {
        onDelete(deductionModel)
      }
    }
  }

  const handleDelete = async () => {
    if (onDelete) {
      if (deductionModel.createdAt) {
        const deleted = await modelStore.deleteDeduction(deductionModel.id)
        if (deleted) {
          onDelete(deleted)
        }
      } else {
        onDelete(deductionModel)
      }
    }
  }

  const handleInfLock = (typename: string) => {
    if (annualInfLock === "User") {
      if (model.getOverrideInflationLock(typename)) {
        setAnnualInfLock("Model")
        setAnnualInf(numberToPercentFormat(model.getDefaultInflationRate(typename)))
      } else {
        setAnnualInfLock(undefined)
      }
    } else if (annualInfLock === "Model") {
      setAnnualInfLock("User")
    } else if (annualInfLock === undefined) {
      setAnnualInfLock("User")
    }
  }

  const renderAnnualInfField = (typename: string) => {
    let endAdornment
    let helperText
    let disabled = isLocked
    if (userStore.isFree) {
      helperText = `Default: ${Model.defaultInflationRate}`
    } else if (annualInfLock === "User") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
            disabled={disabled}
          >
            <LockPersonIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by User"
    } else if (annualInfLock === "Model") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
            disabled={disabled}
          >
            <LockIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by Inflation Strategy"
      disabled = true
    } else {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
            disabled={disabled}
          >
            <LockOpenIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = `Default: ${numberToPercentFormat(model.getDefaultInflationRate(typename), 2)}`
    }

    return (
      <TextFieldValidator
        type="string"
        validators={{ required: false, minValue: 0 }}
        name="annualInf"
        variant="standard"
        margin="dense"
        fullWidth
        label="Annual Inflation %"
        value={annualInf}
        InputProps={{
          endAdornment: endAdornment
        }}
        helperText={helperText}
        disabled={disabled}
        onChange={(event: any) => setAnnualInf(event.target.value)}
      />
    )
  }

  const renderLock = () => {
    if (isLocked) {
      let lockMsg = model.getLock(model.id, "*")
      if (!lockMsg) {
        lockMsg = model.getLock(deductionModel.id, "*")
        if (!lockMsg && income) {
          lockMsg = `Managed by: ${income.description}`
        }
      }
      return (
        <Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
          <LockIcon sx={{color: theme.palette.primary.main, mr:1}}/>
          <Typography variant="body2" color={theme.palette.primary.main}>
            {lockMsg}
          </Typography>
        </Box>
      )

    } else {
      return null
    }
  }

  let assetErrorText
  if (asset && asset.archived && !deductionModel.archived) {
    assetErrorText = "Account is Archived"
  }

  if (!deductionModel || !isOpen) {
    return null
  }

  const endDateHelperText = "Default: Asset Owner's Retirement"

  if (quickForm) {
    return (
      <FormValidator id="deduction-quick-form" onSubmit={handleSave}>
        <Paper elevation={1} sx={{m:0, mb:2, px:2, py:1}}>
          <TextFieldValidator
            margin="normal"
            name="description"
            label="Title"
            type="text"
            fullWidth
            variant="standard"
            required
            validators={{ required: true }}
            value={description}
            onChange={(event: any) => setDescription(event.target.value)}
          />
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <TextFieldValidator
              type="string"
              validators={{ isMoney: true }}
              name="amount"
              variant="standard"
              margin="dense"
              sx={{width:"50%"}}
              label="Amount"
              value={amount}
              onChange={(event: any) => setAmount(event.target.value)}
              InputLabelProps={{shrink: true}}
            />
            <FormGroupSpacer/>
            <TextFieldValidator
              type="string"
              validators={{ required: false, minValue: 0 }}
              name="annualInf"
              variant="standard"
              margin="dense"
              sx={{width:"50%"}}
              label="Annual Inflation %"
              helperText={`Default: 0%`}
              value={annualInf}
              onChange={(event: any) => setAnnualInf(event.target.value)}
            />
          </Box>
          <ScheduleSelector
            value={schedule!}
            startDate={model.getDate(start)}
            onChange={(value: Schedule) => setSchedule(value)}
          />
          <TextFieldValidator
            type="text"
            validators={{ required: false }}
            // required
            name="asset"
            label="Contribution Account"
            variant="standard"
            helperText={assetErrorText}
            error={assetErrorText !== undefined}
            autocompleteOptions={{
              freeSolo: false,
              options: model.getAssetsByCategory(AssetCategory.LiquidInvestableAssets).filter((a: Asset) => !a.assetTypeDef.hasIncomeRider),
              getOptionLabel: (option: Asset) => option ? option.description : "None",
              isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
              value: asset,
              onChange: (event: any, value: Asset, reason: any) => {
                setAsset(value)
                if (value && value.ownerId) {
                  const person = value.ownerId ? model.getPerson(value.ownerId) : undefined
                  if (person) {
                    setOwner(person.nickname)
                  }
                }
              }
            }}
          />
          <TextFieldValidator
            type="text"
            validators={{ required: true }}
            required
            name="owner"
            label="Owner"
            variant="standard"
            autocompleteOptions={{
              freeSolo: false,
              options: model.getPersonNicknames(),
              value: owner,
              disabled: asset?.ownerId !== undefined,
              onChange: (event: any, value: string, reason: any) => {
                setOwner(value)
              }
            }}
          />
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <MilestoneDateFieldValidator
              name="start"
              label="Start Date"
              model={model}
              value={start}
              width="50%"
              onChange={(value: string) => setStart(value)}
            />
            <FormGroupSpacer/>
            <MilestoneDateFieldValidator
              name="end"
              label="End Date"
              model={model}
              value={end}
              width="50%"
              helperText={endDateHelperText}
              onChange={(value: string) => setEnd(value)}
            />
          </Box>
        </Paper>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <DialogButton variant="primary" type="submit" spacing="right" data-testid="deduction-quick-save">
              Save
            </DialogButton>
            <DialogButton variant="secondary" type="button" onClick={handleClose} spacing="right" data-testid="deduction-quick-cancel">
              Cancel
            </DialogButton>
          </Box>
        </Box>
      </FormValidator>
    )
  }

  return (
    <ModelEditDialog title="Edit Deduction" open={isOpen}
                     fullWidth={false}
                     onCancel={handleClose}
                     onSave={!isLocked ? handleSave : undefined}
                     onDelete={!isLocked && onDelete && handleDelete}
                     archived={archived}
                     onArchive={onArchive && !isLocked ? handleArchive : undefined}
    >
      {renderLock()}
      <TextFieldValidator
        margin="normal"
        name="description"
        label="Title"
        type="text"
        fullWidth
        variant="standard"
        required
        validators={{ required: true }}
        value={description}
        disabled={isLocked}
        onChange={(event: any) => setDescription(event.target.value)}
      />
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <TextFieldValidator
          type="string"
          validators={{ isMoney: true }}
          name="amount"
          variant="standard"
          margin="dense"
          sx={{width:"50%"}}
          label="Amount"
          value={amount}
          disabled={isLocked}
          onChange={(event: any) => setAmount(event.target.value)}
        />
        <FormGroupSpacer/>
        <Box pt={1} width="50%">
          <DatePickerValidator
            name="amountDate"
            label="Amount Date"
            value={amountDate}
            format="MM-dd-yyyy"
            variant="standard"
            margin="dense"
            fullWidth
            required
            disabled
            onChange={(newValue: any) => {
              setAmountDate(newValue)
            }}
          />
        </Box>
      </Box>
      <ScheduleSelector
        value={schedule!}
        startDate={model.getDate(start)}
        disabled={isLocked}
        onChange={(value: Schedule) => setSchedule(value)}
      />
      <TextFieldValidator
        type="text"
        validators={{ required: false }}
        // required
        name="asset"
        label="Contribution Account"
        variant="standard"
        helperText={assetErrorText}
        error={assetErrorText !== undefined}
        autocompleteOptions={{
          freeSolo: false,
          options: model.getAssetsByCategory(AssetCategory.LiquidInvestableAssets).filter((a: Asset) => !a.assetTypeDef.hasIncomeRider),
          getOptionLabel: (option: Asset) => option ? option.description : "None",
          isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
          value: asset,
          disabled: isLocked,
          onChange: (event: any, value: Asset, reason: any) => {
            setAsset(value)
            if (value && value.ownerId) {
              const person = value.ownerId ? model.getPerson(value.ownerId) : undefined
              if (person) {
                setOwner(person.nickname)
              }
            }
          }
        }}
      />
      <TextFieldValidator
        type="text"
        validators={{ required: true }}
        required
        name="owner"
        label="Owner"
        variant="standard"
        autocompleteOptions={{
          freeSolo: false,
          options: model.getPersonNicknames(),
          value: owner,
          disabled: isLocked || asset?.ownerId !== undefined,
          onChange: (event: any, value: string, reason: any) => {
            setOwner(value)
          }
        }}
      />
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <MilestoneDateFieldValidator
          name="start"
          label="Start Date"
          model={model}
          value={start}
          disabled={isLocked}
          width="50%"
          onChange={(value: string) => setStart(value)}
        />
        <FormGroupSpacer/>
        <MilestoneDateFieldValidator
          name="end"
          label="End Date"
          model={model}
          value={end}
          disabled={isLocked}
          width="50%"
          helperText={endDateHelperText}
          onChange={(value: string) => setEnd(value)}
        />
      </Box>
      {renderAnnualInfField("deduction")}
    </ModelEditDialog>
  )
}

export default DeductionEditDialog